import type { PlayerMapDTO, ViewRect } from '@kartothek/shared';
import { useQuery } from '@tanstack/react-query';
import type Konva from 'konva';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Group, Image as KImage, Layer, Rect, Shape, Stage, Text } from 'react-konva';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import { drawGrid, FogLayer, TemplateNode, TokenNode } from '../components/MapView';
import { t } from '../i18n';
import { connectEvents } from '../realtime';

interface View {
  scale: number;
  x: number;
  y: number;
}

function useImage(url: string | null) {
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  useEffect(() => {
    if (!url) return setImg(null);
    const el = new window.Image();
    el.onload = () => setImg(el);
    el.src = url;
  }, [url]);
  return img;
}

function PlayerMap({ token, map }: { token: string; map: PlayerMapDTO }) {
  // Neues Bild nur laden, wenn die Karte wechselt
  const img = useImage(`/api/screen/${encodeURIComponent(token)}/image?m=${map.noteId}`);
  const wrapRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setSize({ width: Math.round(e!.contentRect.width), height: Math.round(e!.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fitTo = useCallback(
    (r: ViewRect | null) => {
      if (!img || !size.width) return;
      const rect = r ?? { x: 0, y: 0, w: img.width, h: img.height };
      const scale = Math.min(size.width / rect.w, size.height / rect.h);
      setView({ scale, x: size.width / 2 - (rect.x + rect.w / 2) * scale, y: size.height / 2 - (rect.y + rect.h / 2) * scale });
    },
    [img, size.width, size.height],
  );

  // Der DM gibt den Ausschnitt vor – bei jeder Änderung neu einpassen
  const viewKey = JSON.stringify(map.view);
  useEffect(() => fitTo(map.view), [fitTo, viewKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const onWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const pointer = stageRef.current?.getPointerPosition();
    if (!pointer) return;
    const factor = e.evt.deltaY > 0 ? 1 / 1.15 : 1.15;
    setView((v) => {
      const scale = Math.min(8, Math.max(0.05, v.scale * factor));
      return { scale, x: pointer.x - ((pointer.x - v.x) / v.scale) * scale, y: pointer.y - ((pointer.y - v.y) / v.scale) * scale };
    });
  };

  const cell = map.grid.type === 'none' ? 70 : map.grid.size;
  const inv = 1 / view.scale;
  return (
    <div className="screen-map" ref={wrapRef}>
      {img && size.width > 0 && (
        <Stage
          ref={stageRef}
          width={size.width}
          height={size.height}
          x={view.x}
          y={view.y}
          scaleX={view.scale}
          scaleY={view.scale}
          draggable
          onWheel={onWheel}
          onDragMove={(e) => e.target === e.target.getStage() && setView((v) => ({ ...v, x: e.target.x(), y: e.target.y() }))}
          onDragEnd={(e) => e.target === e.target.getStage() && setView((v) => ({ ...v, x: e.target.x(), y: e.target.y() }))}
        >
          <Layer listening={false}>
            <KImage image={img} />
            {map.grid.type !== 'none' && (
              <Shape
                stroke={map.grid.color}
                opacity={map.grid.opacity}
                strokeWidth={1}
                strokeScaleEnabled={false}
                sceneFunc={(ctx, shape) => {
                  drawGrid(ctx, map.grid, img.width, img.height);
                  ctx.strokeShape(shape);
                }}
              />
            )}
          </Layer>
          {map.fog.enabled && <FogLayer fog={map.fog} draft={null} width={img.width} height={img.height} gm={false} />}
          <Layer listening={false}>
            {map.templates.map((tp) => (
              <TemplateNode key={tp.id} template={tp} grid={map.grid} scale={view.scale} />
            ))}
          </Layer>
          <Layer listening={false}>
            {map.pins.map((p) => {
              const w = Math.max(26, 10 + p.label.length * 7.5);
              return (
                <Group key={p.id} x={p.x} y={p.y} scaleX={inv} scaleY={inv}>
                  <Rect x={-w / 2} y={-13} width={w} height={26} cornerRadius={13} fill={p.color ?? '#e8b04a'} stroke="#1b1208" strokeWidth={2} shadowBlur={6} shadowOpacity={0.6} />
                  {p.label && <Text text={p.label} fontSize={12} fontStyle="bold" fill="#1b1208" width={w} height={26} offsetX={w / 2} offsetY={13} align="center" verticalAlign="middle" wrap="none" />}
                </Group>
              );
            })}
          </Layer>
          <Layer>
            {map.tokens.map((tk) => (
              <TokenNode key={tk.id} token={tk} grid={map.grid} cell={cell} scale={view.scale} selected={false} draggable={false} onSelect={() => undefined} onMove={() => undefined} />
            ))}
          </Layer>
        </Stage>
      )}
      <div className="screen-tools">
        <button onClick={() => fitTo(map.view)}>⤢ {t('screen.fit')}</button>
        <button onClick={() => void document.documentElement.requestFullscreen?.()}>⛶ {t('screen.fullscreen')}</button>
      </div>
    </div>
  );
}

/** Spieleransicht über den geheimen Link – ohne Login, ohne DM-Inhalte. */
export function ScreenPage() {
  const { token = '' } = useParams();
  const q = useQuery({ queryKey: ['player-screen', token], queryFn: () => api.screen(token), retry: false, refetchInterval: 60_000 });
  const refetch = q.refetch;

  useEffect(() => connectEvents(`/api/ws/screen/${encodeURIComponent(token)}`, () => void refetch()), [token, refetch]);
  useEffect(() => {
    document.title = q.data ? `${q.data.campaignName} · Kartothek` : 'Kartothek';
  }, [q.data]);

  if (q.isLoading) return <div className="center-screen muted">{t('common.loading')}</div>;
  if (q.isError) return <div className="center-screen muted">{t('screen.notFound')}</div>;
  const data = q.data!;
  if (!data.map) {
    return (
      <div className="center-screen screen-waiting">
        <h1>{data.campaignName}</h1>
        <p className="muted">{t('screen.waiting')}</p>
      </div>
    );
  }
  return <PlayerMap token={token} map={data.map} />;
}
